import env from '@polleverywhere/core/lib/env'
import { deepDefaults } from '@polleverywhere/core/lib/util'
import URI from 'urijs'
import _ from 'underscore'

function getScreenshotUrl (activity, options = {}) {
  const cdnUrl = getPlaceholderCdnUrl(activity, options)
  return URI(cdnUrl)
    .setQuery('hide', 'overlay,metadata,qr_code')
    .toString()
}

function escapeBackslashes (activityTitle) {
  return activityTitle.replace(/[\\]/g, '\\$&')
}

function getActivityUrl (activity, options = {}) {
  _.defaults(options, {
    query: {},
    persist: false
  })

  const uri = env
    .get('api_url')
    .path(activity.showUrl())
    .protocol(env.get('secure_protocol'))

  uri.query(options.query)

  if (options.persist) {
    uri.addQuery('onscreen', 'persist')
  }

  return uri.toString()
}

function getPlaceholderCdnUrl (activity, options = {}) {
  return URI(getPlaceholderUrl(activity, options))
    .path('placeholder_cdn_url')
    .toString()
}

function aspectRatioToSize (aspectRatio) {
  // 4:3 aspect ratio comes out to a width/height ratio of 1.33...
  if (aspectRatio > 1.33 && aspectRatio < 1.34) {
    return {
      width: 1200,
      height: 900
    }
  }

  return {
    width: 1600,
    height: 900
  }
}

function getPlaceholderUrl (activity, options = {}) {
  deepDefaults(options, {
    placeholderFormat: 'tiny',
    aspectRatio: 1.33,
    activityQuery: {
      hide: 'instructions,empty_ftp_notice,correctness,clickable_image_regions,keywords',
      display_state: 'chart',
      archive_id: 0
    }
  })

  const url = getActivityUrl(activity, {
    query: options.activityQuery,
    persist: false
  })

  const { width, height } = aspectRatioToSize(options.aspectRatio)
  return env
    .get('slide_server_url')
    .path('placeholder')
    .query({
      url,
      width,
      height,
      brand: 'polleverywhere',
      format: options.placeholderFormat,
      hide: options.hide
    })
    .toString()
}

// Converts slide objects {title, activityUrl, placeholderUrl} to native
// array based format [title, activityUrl, placeholderUrl, screenshotUrl]
function convertSlidesToNativeFormat (slides) {
  const titles = []
  const activityUrls = []
  const placeholderUrls = []
  const screenshotUrls = []

  slides.forEach(({ title, activityUrl, placeholderUrl, screenshotUrl }) => {
    titles.push(title)
    activityUrls.push(activityUrl)
    placeholderUrls.push(placeholderUrl)
    screenshotUrls.push(screenshotUrl)
  })

  return [titles, activityUrls, placeholderUrls, screenshotUrls]
}

// Get a slide object for an activity.
// Allows passing in options for activityUrl and placeholderUrl generators
function getActivitySlide (model, options = {}) {
  // Merge and apply all default URL parameter values to each slide.
  deepDefaults(options, this.options.urlOptions)

  const query = options.activityUrl.query
  if (options.activityUrl.persist) {
    query.onscreen = 'persist'
  }

  return {
    model,
    query,
    title: escapeBackslashes(model.cleanTitle().toString()),
    activityUrl: getActivityUrl(model, options.activityUrl),
    placeholderUrl: getPlaceholderCdnUrl(model, options.placeholderUrl),
    screenshotUrl: getScreenshotUrl(model, options.placeholderUrl),
    modelOverrides: options.modelOverrides
  }
}

/**
 * Generates activity slides for the specified competition poll. Different slide
 * sets are generated based on `this.options.target` type.
 * @param {Backbone.Model.Poll} poll Competition poll
 * @return {Object[]} Array of slide objects.
 */
function getCompetitionPollSlides (poll) {
  const slides = []

  switch (this.options.target) {
    case 'presentation':
      // Question title view.
      slides.push(getActivitySlide.call(this, poll, {
        activityUrl: {
          persist: true,
          query: {
            display_state: 'title',
            controls: 'none'
          }
        },
        placeholderUrl: {
          activityQuery: {
            display_state: 'title'
          }
        },
        modelOverrides: {
          state: 'closed',
          display_state: 'title'
        }
      }))

      // Question instructions view.
      slides.push(getActivitySlide.call(this, poll, {
        activityUrl: {
          persist: true,
          query: {
            display_state: 'instructions',
            controls: 'none'
          }
        },
        placeholderUrl: {
          activityQuery: {
            display_state: 'instructions'
          }
        },
        modelOverrides: {
          state: 'opened',
          display_state: 'instructions',
          flash_options: {
            showInstructions: true
          }
        }
      }))

      // Question results view w/ correctness.
      slides.push(getActivitySlide.call(this, poll, {
        activityUrl: {
          persist: true,
          query: {
            display_state: 'chart',
            display: 'correctness',
            controls: 'none'
          }
        },
        placeholderUrl: {
          activityQuery: {
            display_state: 'chart'
          }
        },
        modelOverrides: {
          state: 'closed',
          display_state: 'chart',
          show_correctness: true
        }
      }))
      break

    case 'results':
      // For competition polls, show only results in chart view w/ correctness.
      slides.push(getActivitySlide.call(this, poll, {
        activityUrl: {
          query: {
            display_state: 'chart',
            display: 'correctness'
          }
        },
        placeholderUrl: {
          activityQuery: {
            display_state: 'chart',
            display: 'correctness'
          }
        },
        modelOverrides: {
          state: 'closed',
          display_state: 'chart',
          show_correctness: true
        }
      }))
      break

    default:
      // Add default behavior here if/when we ever need one.
      break
  }

  return slides
}

/**
 * Generate activity slides for a survey.
 * It consists of a horse race and 1 slide per question
 */
function getSurveySlides (model, options) {
  const slides = []

  slides.push(getActivitySlide.call(this, model, {
    activityUrl: options.activityUrl,
    placeholderUrl: options.placeholderUrl,
    modelOverrides: {
      state: 'opened'
    }
  }))

  // For each survey question add a slide with the instructions hidden
  model.polls.forEach((poll, index) => {
    const data = getActivitySlide.call(this, poll, {
      activityUrl: deepDefaults({}, options.activityUrl, {
        query: {
          hide: 'instructions'
        }
      }),
      placeholderUrl: options.placeholderUrl,
      modelOverrides: {
        state: 'closed',
        display_state: 'chart',
        flash_options: {
          showInstructions: false
        }
      }
    })

    slides.push(data)
  })

  return slides
}

/**
 * Generates activity slides for the specified competition. Different slide sets
 * are generated based on `this.options.target` type.
 * @param {Backbone.Model.Poll} poll Competition poll
 * @return {Object[]} Array of slide objects.
 */
function getCompetitionSlides (model) {
  const slides = []

  switch (this.options.target) {
    case 'presentation':
      // When presenting competitions, show the competition welcome screen
      // followed by various slide states for each question.
      slides.push(getActivitySlide.call(this, model, {
        activityUrl: {
          persist: true,
          query: {
            display_state: 'welcome',
            controls: 'none'
          }
        },
        placeholderUrl: {
          activityQuery: {
            display_state: 'welcome'
          }
        },
        modelOverrides: {
          state: 'opened',
          display_state: 'welcome'
        }
      }))

      // For each competition question, add slides for various display states.
      model.polls.forEach((poll, index) => {
        slides.push(...getCompetitionPollSlides.call(this, poll))

        // leaderboard / finale
        const displayState = index === model.polls.length - 1
          ? 'finale' : 'leaderboard'
        slides.push(getActivitySlide.call(this, model, {
          activityUrl: {
            persist: true,
            query: {
              display_state: displayState
            }
          },
          placeholderUrl: {
            activityQuery: {
              display_state: displayState
            }
          },
          modelOverrides: {
            state: 'closed',
            display_state: displayState
          }
        }))
      })
      break

    case 'results':
      // For competition results, start with finale leaderboard.
      slides.push(getActivitySlide.call(this, model, {
        activityUrl: {
          query: {
            display_state: 'finale'
          }
        },
        placeholderUrl: {
          activityQuery: {
            display_state: 'finale'
          }
        },
        modelOverrides: {
          display_state: 'finale'
        }
      }))

      model.polls.forEach((poll, index) => {
        slides.push(...getCompetitionPollSlides.call(this, poll))
      })
      break

    default:
      // Add default behavior here if/when we ever need one.
      break
  }

  return slides
}

/**
 * SlideSet is a class that generates a set of slide data
 * for a list of activities
 */
export default class SlideSet {
  /**
   * Constructor
   *
   * @param {Array<Model>} activities List of Poll|Survey|Competition models
   * @param {Object} options
   * @param {string} [options.target='presentation'] Target output types will
   *    generate slide sets based on pre-defined templates or groupings.
   *    Available target types to choose from are:
   *    - 'presentation' => All slides necessary to present activities
   *    - 'results' => Only slides relevant for displaying results
   * @param {Object} options.urlOptions Default parameters for activity and
   *    placeholder URLs, which will be applied automatically to all slides.
   * @param {Object} options.urlOptions.activityUrl Activity URL options
   * @param {Object} options.urlOptions.placeholderUrl Placeholder URL options
   */
  constructor (activities = [], options = {}) {
    this.addActivity = this.addActivity.bind(this)
    this.getData = this.getData.bind(this)

    this.options = deepDefaults(options, {
      env: env,
      target: 'presentation',

      // Declare default URL param values to be applied on each slide.
      urlOptions: {
        activityUrl: {},
        placeholderUrl: {
          placeholderFormat: 'tiny',
          hide: 'qr_code',
          aspectRatio: 1600 / 900,
          activityQuery: {
            animation: false,
            hide: [
              'instructions',
              'empty_ftp_notice',
              'correctness',
              'clickable_image_regions',
              'keywords'
            ].join(','),
            display_state: 'chart',
            archive_id: 0
          }
        }
      }
    })

    /**
     * Holds slide data.
     * @type {Array}
     */
    this.slideData = []

    // Add each activity via constructor to the list.
    _.flatten([activities]).forEach((activity) => this.addActivity(activity))
  }

  /**
   * Add and generate slides for the specified activity.
   * @param {Poll|Survey|Competition} activity
   * @param {Object} [options={}] Configurable options.
   * @param {Object} [options.expand=true] By default, activities may be
   *    "expanded" to generate multiple slides. For example, competition
   *    activities will generate multiple slides depending on the number of
   *    polls it contains.  Setting `expand=false` prevents expansion and
   *    instead adds a single activity slide for the given `activity` and using
   *    optional `activityUrl` and `placeholderUrl` options to configure custom
   *    slide URL params.
   * @param {Object} [options.activityUrl] When `expand=false`, generate a
   *    single activity slide using these custom activity URL params.
   * @param {Object} [options.placeholderUrl] When `expand=false`, generate
   *    single activity slide using these custom placeholder URL params
   */
  addActivity (activity, options = {}) {
    _.defaults(options, {
      expand: true,
      activityUrl: {},
      placeholderUrl: {}
    })

    let slideData

    if (options.expand && activity.type() === 'competition') {
      slideData = getCompetitionSlides.call(this, activity)
    } else if (options.expand && activity.type() === 'survey') {
      slideData = getSurveySlides.call(this, activity, options)
    } else if (options.expand && activity.has('competition_id')) {
      slideData = getCompetitionPollSlides.call(this, activity)
    } else {
      // Catch-all for all non-MQAs or surveys where 1-1 activity to slides are
      // to be generated.
      slideData = getActivitySlide.call(this, activity, {
        activityUrl: options.activityUrl,
        placeholderUrl: options.placeholderUrl
      })
    }

    this.slideData = this.slideData.concat(_.flatten([slideData]))
  }

  /**
   * Get slide data
   *
   * @param {Object} options
   * @param {String} options.format Output format. (simple | native) (Default: 'simple')
   *
   * @return {Array} An array of slide data objects when format is 'simple', or [titles, activityUrls, placeholderUrls, screenshotUrls] when format is 'native'
   */
  getData (options = {}) {
    _.defaults(options, {
      format: 'simple'
    })

    if (options.format === 'native') {
      return convertSlidesToNativeFormat(this.slideData)
    } else {
      return this.slideData
    }
  }
}
